import React from 'react';
import { ChevronLeft, Lock, Percent, Plus, Power, PowerOff } from 'lucide-react';
import styles from './DiscountMembershipList.module.css';

/**
 * canCreate  -> "Create pass" buttons
 * canEdit    -> "Edit" on each row
 * canDeactivate -> Activate / Deactivate on each row
 * All default to false: nothing shows unless access is given.
 */
export default function DiscountMembershipList({
    memberships = [], onAddNew, onEdit, onToggleStatus, onBack, isLoading,
    canCreate = false, canEdit = false, canDeactivate = false,
}) {
    const hasRowActions = canEdit || canDeactivate;

    return (
        <div className={styles.listContainer}>
            <div className={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button type="button" className={styles.backIconBtn} onClick={onBack} title="Go back" aria-label="Back">
                        <ChevronLeft size={18} />
                    </button>
                    <div>
                        <h2>Discount Memberships</h2>
                        <p>{hasRowActions || canCreate ? 'Manage profile-based subscription passes and discount rules.' : 'Discount passes available for clients (view only).'}</p>
                    </div>
                </div>
                {canCreate ? (
                    <button type="button" className={styles.primaryBtn} onClick={onAddNew}>
                        <Plus size={14} /> Create pass
                    </button>
                ) : (!hasRowActions && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, background: '#f8f9fa', color: '#797980', fontSize: 11, fontWeight: 800 }}>
                        <Lock size={11} /> View only
                    </span>
                ))}
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Pass name</th>
                            <th>Status</th>
                            <th>Client pays</th>
                            <th>Validity</th>
                            <th>Benefits</th>
                            {hasRowActions && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={hasRowActions ? 6 : 5}>
                                    <div className={styles.emptyStateContainer} style={{ padding: '30px' }}>
                                        <div className={styles.spinner} />
                                        <span style={{ marginTop: '12px' }}>Loading plans...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : memberships.length === 0 ? (
                            <tr>
                                <td colSpan={hasRowActions ? 6 : 5}>
                                    <div className={styles.emptyStateContainer}>
                                        <Percent size={32} style={{ opacity: 0.5, marginBottom: '12px' }} />
                                        {canCreate ? 'No discount passes yet. Click "Create pass" to begin.' : 'No discount passes yet.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            memberships.map((m) => {
                                const isActive = m.status === 'ACTIVE';
                                return (
                                    <tr key={m.id} className={!isActive ? styles.rowInactive : ''}>
                                        <td>
                                            <div style={{ fontWeight: 800, color: isActive ? '#18181a' : '#797980' }}>{m.name}</div>
                                            <span className={styles.tierSubtext}>{m.membershipType} tier</span>
                                        </td>
                                        <td>
                                            <span className={isActive ? styles.statusActive : styles.statusArchived}>
                                                <span className={styles.statusDot} />
                                                {isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 800, color: isActive ? '#18181a' : '#797980' }}>₹{Number(m.price || 0).toLocaleString('en-IN')}</td>
                                        <td style={{ fontWeight: 600, color: isActive ? '#36363b' : '#797980' }}>{m.validity} {m.validityType}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px', opacity: isActive ? 1 : 0.5 }}>
                                                <span className={styles.badgeDiscount}>{m.serviceDiscount}% services</span>
                                                <span className={styles.badgeDiscount}>{m.productDiscount}% products</span>
                                            </div>
                                        </td>
                                        {hasRowActions && (
                                            <td>
                                                <div className={styles.actionGroup}>
                                                    {canEdit && (
                                                        <button type="button" className={styles.actionBtn} onClick={() => onEdit(m)}>Edit</button>
                                                    )}
                                                    {canDeactivate && (
                                                        <button
                                                            type="button"
                                                            className={isActive ? styles.actionBtnDanger : styles.actionBtnSuccess}
                                                            onClick={() => {
                                                                if (window.confirm(isActive ? `Stop selling "${m.name}"? Clients who have it keep it till it ends.` : `Start selling "${m.name}" again?`)) onToggleStatus(m.id);
                                                            }}
                                                            title={isActive ? 'Stop new sales' : 'Sell again'}
                                                        >
                                                            {isActive ? <PowerOff size={13} /> : <Power size={13} />}
                                                            {isActive ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}